import * as THREE from 'three';
import type { FloraCatalog } from '@/flora/FloraCatalog';
import type { FloraInstance } from '@/flora/FloraInstance';
import { FloraWorld } from '@/flora/FloraWorld';
import type { FloraResolver } from '@/localization/FloraResolver';
import type { SiteObject } from '@/site/SiteObject';
import type { Terrain } from '@/terrain/Terrain';
import { CommandHistory } from '@/core/CommandHistory';
import { SelectionManager } from './SelectionManager';
import { SelectionRenderer } from '@/rendering/SelectionRenderer';
import { PlacementTool } from './PlacementTool';
import { otherPlantMode, type PlantToolMode } from './PlantBrush';
import { FloraTransformSubject, TransformTool, type TransformSubject } from './TransformTool';
import { StaffageTransformSubject } from './StaffageTransform';
import { isMovable, type StaffageLayer, type StaffageRecord } from './StaffageLayer';
import type { Command } from '@/core/Command';
import { CompositeCommand } from '@/core/CompositeCommand';
import {
  AddFloraCommand,
  AddManyFloraCommand,
  RemoveFloraCommand,
  TransformFloraCommand,
  type FloraChange,
} from './FloraCommands';
import { idsInScreenRect, idsOfSpecies, rectFromCorners, type ScreenPoint } from './SelectionQuery';
import { duplicateInstances } from './DuplicateTool';
import { ClipboardManager } from './ClipboardManager';
import { AliasManager } from './AliasManager';
import { GroupManager, type PlantGroup } from './GroupManager';
import { resolveShortcut, toShortcutEvent, type ShortcutAction } from './KeyboardShortcuts';

/** Optional instance fields the inspector is allowed to clear back to "unset". */
export type ClearableInstanceField =
  | 'seasonOverrideDayOfYear'
  | 'containerStage'
  | 'aliasGroupId'
  | 'groupId';

/** A single inspector-driven edit to one placed plant. */
export interface FloraInstanceEdit {
  id: string;
  patch: Partial<Omit<FloraInstance, 'id' | 'floraDefinitionId'>>;
  clear?: readonly ClearableInstanceField[];
}

/** Fired whenever the editor's observable state changes, so the HUD can re-render. */
export type EditorChangeListener = () => void;

/**
 * Fired when the *document* changed — a plant placed, moved, grouped, linked, or the ground
 * sculpted — as opposed to {@link EditorChangeListener}, which also fires for view-only changes such
 * as selection or arming a tool. Autosave listens here, so clicking a plant is not an edit.
 */
export type EditorDocumentListener = () => void;

export interface FloraEditorDeps {
  domElement: HTMLElement;
  getCamera: () => THREE.Camera;
  floraRoot: THREE.Object3D;
  overlayRoot: THREE.Object3D;
  terrain: () => Terrain | null;
  catalog: FloraCatalog;
  /**
   * The site's flora resolver, so planted specimens are drawn against this site's climate rather
   * than against the catalog's reference one. Omitted in tests, where the reference climate is the
   * right answer and reproduces the app's pre-localization behaviour.
   */
  resolver?: FloraResolver;
  /** Buildings and paving that refuse planting. Optional — a site may have none. */
  siteObjects?: () => readonly SiteObject[];
  /**
   * The other classes of placed thing this editor selects, moves and deletes alongside plants —
   * creatures and objects. Optional, and absent in most tests, where the flora-only editor is both
   * the thing under test and the behaviour that predates staffage.
   *
   * They are handed to the *flora* editor rather than given an editor of their own because there is
   * one selection, one history and one Delete key on a site. Two selection managers would mean two
   * highlight sets, two answers to "what does Escape clear", and an undo stack that interleaves.
   */
  staffage?: readonly StaffageLayer[];
  /**
   * The scene root creatures and objects are drawn under, so a click can pick them. Separate from
   * `floraRoot` because the two are separate groups in the scene, and the season model walks one of
   * them (see `Application`).
   */
  staffageRoot?: THREE.Object3D;
  /** Optional DOM element for the placement/selection readout. */
  readout?: HTMLElement;
  /**
   * A short line to show beside the pointer for a few seconds — which planting mode just armed,
   * what a brush stroke planted. The HUD draws it; the editor only relays what the tools say.
   */
  onNotice?: (text: string) => void;
  /** Optional app-level hooks for shortcuts the editor itself doesn't own. */
  onSave?: () => void;
  onSaveAs?: () => void;
  onRename?: () => void;
  onFocusSelection?: (ids: string[]) => void;
  onSetView?: (view: 'perspective' | 'top' | 'front') => void;
  onTogglePhotoMode?: () => void;
  onPaintTool?: () => void;
  onSculptTool?: () => void;
  onWandTool?: () => void;
  onTagTool?: () => void;
  /** Show or hide the reference grid. A view toggle — it takes the canvas from no one. */
  onToggleGrid?: () => void;
  /** Show or hide the topographic contour map. A view toggle, like the grid. */
  onToggleTopo?: () => void;
  /** Show or hide the scene pointers — the manifest's tag numbers, drawn on the garden. */
  onTogglePointers?: () => void;
  /** Show or hide the operations history. Holds no canvas — see `HistoryPanel`. */
  onToggleHistory?: () => void;
  /**
   * Called when a flora tool is asked for while another tool holds the canvas. The app hands it
   * back; without this, pressing `P` while painting would silently do nothing, which reads as a
   * broken keyboard rather than as a tool that is busy.
   */
  onRequestCanvas?: () => void;
}

/**
 * Shortcuts that keep working while a ground tool holds the canvas.
 *
 * Everything else is suppressed, because the ground tools want the same bare keys the flora editor
 * uses — `[` and `]` size the brush *and* cycle the plant palette — and a key that quietly does two
 * things is worse than one that does none. What survives is the set that is about the document
 * rather than about the tool: history, saving, and the keys that switch tools in the first place,
 * so there is always a way back out.
 */
const SHORTCUTS_WHILE_SUSPENDED: readonly ShortcutAction[] = [
  'undo',
  'redo',
  // The history panel is the long form of undo and redo, and survives suspension for exactly the
  // reason they do: it is about the document, not about the tool in hand.
  'toggle-history',
  'save',
  'save-as',
  'rename',
  'paint-ground',
  'sculpt-ground',
  'wand',
  'tag',
  // The tool keys all survive, so any tool is one key away from any other; the flora ones take the
  // canvas back on the way in (`onRequestCanvas`).
  'place',
  // And with them the key that chooses *which* planting tool, since it arms placement itself.
  'plant-mode',
  'move',
  'rotate',
  'maturity',
  'view-perspective',
  'view-top',
  'view-front',
  'photo-mode',
  // Reading coordinates off the ground is most wanted while a tool is live — the brush and the wand
  // are exactly when you want to know where you are — so the grid key survives suspension too.
  'toggle-grid',
  // And the contour map for the same reason, more so: the tool that most wants it is the sculpt
  // brush, which is precisely one of the tools doing the suspending.
  'toggle-topo',
  // Naming what is already planted is most wanted while planting more of it — the question the
  // callouts answer is "what did I put here", and it is asked with the plant tool in hand.
  'toggle-pointers',
];

/**
 * What a drag is called in the history when it moved more than one class of thing at once. A drag
 * that touched only one class is named by that class instead — "Move eastern cottontail".
 */
const TRANSFORM_LABELS: Record<'move' | 'rotate' | 'maturity', string> = {
  move: 'Move',
  rotate: 'Turn',
  maturity: 'Grow',
};

/**
 * Composes the Milestone 3 flora editing loop: placement, selection, transform, and highlight
 * rendering (spec §3.5–§3.6). Coordinates input so a single click either places, selects, or
 * transforms — never several at once.
 *
 * ## It selects more than plants
 *
 * Creatures and objects arrive as {@link StaffageLayer}s and are picked, highlighted, dragged and
 * deleted through the same selection, the same history and the same keys. Only *planting* is
 * flora-only — the palette, the brush, maturity, aliases and groups all still mean one thing.
 *
 * The name is now narrower than the class. Renaming it would touch every test and every call site
 * for no behavioural gain, so it is left alone and said out loud here instead.
 */
export class FloraEditor {
  readonly world: FloraWorld;
  readonly selection = new SelectionManager();
  readonly history = new CommandHistory();
  /** Named plant groups. Public so the project document can serialize and restore them. */
  readonly groups: GroupManager;
  private readonly clipboard = new ClipboardManager();
  private readonly alias: AliasManager;
  private readonly selectionRenderer: SelectionRenderer;
  private readonly placement: PlacementTool;
  private readonly transform: TransformTool;
  private readonly pickRaycaster = new THREE.Raycaster();
  /** Creatures and objects, in the order a click resolves them. Empty in a flora-only editor. */
  private readonly staffage: readonly StaffageLayer[];
  private readonly subjects: readonly TransformSubject[];

  private activeId: string;
  private inputEnabled = true;
  private readonly changeListeners = new Set<EditorChangeListener>();
  private readonly documentListeners = new Set<EditorDocumentListener>();

  private readonly onPointerDown = (e: PointerEvent): void => this.handlePick(e);
  private readonly onPointerMove = (e: PointerEvent): void => this.handleBoxMove(e);
  private readonly onPointerUp = (e: PointerEvent): void => this.handleBoxUp(e);
  private readonly onDoubleClick = (e: MouseEvent): void => this.handleDoubleClick(e);
  private readonly onKeyDown = (e: KeyboardEvent): void => this.handleKey(e);

  private boxStart: { x: number; y: number } | null = null;
  private boxActive = false;
  private boxAdditive = false;
  private boxElement: HTMLDivElement | null = null;

  constructor(private readonly deps: FloraEditorDeps) {
    this.activeId = deps.catalog.list()[0]?.id ?? '';
    this.world = new FloraWorld(deps.catalog, deps.floraRoot, deps.resolver);
    this.alias = new AliasManager(this.world);
    this.groups = new GroupManager(this.world);
    this.staffage = deps.staffage ?? [];
    this.selectionRenderer = new SelectionRenderer((id) => this.viewOf(id));
    deps.overlayRoot.add(this.selectionRenderer.object);

    this.placement = new PlacementTool({
      domElement: deps.domElement,
      getCamera: deps.getCamera,
      terrain: deps.terrain,
      world: this.world,
      catalog: deps.catalog,
      onPlace: (instance) => this.placeInstance(instance),
      // A brush stroke is drawn as it is made and recorded once at the end: the plants go straight
      // into the world here, and the history hears about the whole drift below.
      onPaintPlace: (instance) => {
        this.world.add(instance);
      },
      onPaintCommit: (instances) => this.recordStroke(instances),
      onChange: () => this.emitChange(),
      ...(deps.onNotice ? { onNotice: deps.onNotice } : {}),
      ...(deps.siteObjects ? { siteObjects: deps.siteObjects } : {}),
      ...(deps.readout ? { readout: deps.readout } : {}),
    });
    deps.overlayRoot.add(this.placement.overlayObject);

    // Plants first, so a mixed drag anchors on the plant when a plant is primary — and so the flora
    // path stays exactly what it was before creatures and objects joined the selection.
    this.subjects = [
      new FloraTransformSubject(this.world, deps.terrain, (changes) => {
        // Shared props (e.g. maturity) propagate to linked aliases (spec §4.5).
        for (const change of changes) this.alias.propagate(change.after.id);
      }),
      ...this.staffage.map((layer) => new StaffageTransformSubject(layer)),
    ];

    this.transform = new TransformTool({
      domElement: deps.domElement,
      getCamera: deps.getCamera,
      terrain: deps.terrain,
      selection: this.selection,
      subjects: () => this.subjects,
      onCommit: (commands, mode) => {
        // Changes were applied live during the drag; record without re-executing.
        this.history.push(this.oneEntry(commands, `${TRANSFORM_LABELS[mode]} selection`));
        this.refresh();
      },
    });

    this.selection.onChange((change) => {
      this.selectionRenderer.update(change.selected);
      this.emitChange();
    });
    // Every add/remove/transform funnels through the history, so this is the single hook the HUD
    // needs to stay in sync with the world — and, because ground strokes share this history, the
    // single hook that says the site model changed at all.
    this.history.onChange(() => {
      this.emitChange();
      this.emitDocumentChange();
    });

    deps.domElement.addEventListener('pointerdown', this.onPointerDown);
    deps.domElement.addEventListener('dblclick', this.onDoubleClick);
    window.addEventListener('keydown', this.onKeyDown);
    this.updateReadout();
  }

  /** Keep selection highlights aligned with (possibly rebuilt) views. */
  refresh(): void {
    this.selectionRenderer.update(this.selection.selected());
  }

  /* ---------------- Public surface consumed by the HUD ---------------- */

  get catalog(): FloraCatalog {
    return this.deps.catalog;
  }

  get activeDefinitionId(): string {
    return this.activeId;
  }

  get isPlacing(): boolean {
    return this.placement.isActive;
  }

  /** The planting mode that persists between strokes — what the status bar reports. */
  get plantMode(): PlantToolMode {
    return this.placement.plantMode;
  }

  /** The mode the next press will use, held `Ctrl` included — what the pointer draws. */
  get effectivePlantMode(): PlantToolMode {
    return this.placement.effectiveMode;
  }

  /** The planting brush's reach and spacing, for the status bar's readout. */
  get plantBrush(): { radiusMeters: number; spacingMeters: number } {
    return {
      radiusMeters: this.placement.brushShape.radiusMeters,
      spacingMeters: this.placement.spacingMeters,
    };
  }

  setPlantMode(mode: PlantToolMode): void {
    this.placement.setPlantMode(mode);
  }

  /** Swap spot planting for the brush, or back. The toolbar's and the keyboard's way in. */
  togglePlantMode(): void {
    // Arming first, so the key means the same thing whether or not the tool is already in hand:
    // otherwise pressing it with nothing armed would silently change a mode you cannot see.
    if (!this.placement.isActive) {
      this.startPlacement();
      this.placement.setPlantMode(otherPlantMode(this.placement.plantMode));
      return;
    }
    this.placement.togglePlantMode();
  }

  /**
   * Advance any brush stroke in flight. Called once per frame by the application, because a stroke
   * is paced by the clock rather than by pointer events — see `TerrainBrush`.
   */
  tick(deltaSeconds: number): void {
    this.placement.tick(deltaSeconds);
  }

  get activeTransformMode(): string | null {
    return this.transform.activeMode;
  }

  /** True while another tool (paint, sculpt) holds the canvas. */
  get isSuspended(): boolean {
    return !this.inputEnabled;
  }

  /**
   * Hand the canvas to another tool, or take it back.
   *
   * The flora editor keeps its listeners attached rather than detaching them, so that the shortcuts
   * that still make sense while painting — undo, save, switching tool — keep working. What it stops
   * doing is picking, box-selecting and claiming the bare keys the ground tools need.
   */
  setInputEnabled(enabled: boolean): void {
    if (this.inputEnabled === enabled) return;
    this.inputEnabled = enabled;
    if (!enabled) {
      this.cancelPlacement();
      this.transform.cancel();
      this.boxStart = null;
      this.boxActive = false;
      this.removeBoxElement();
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
    }
    this.emitChange();
  }

  /**
   * Re-seat plants onto the ground after the sculpt tool has moved it.
   *
   * A plant's height is *derived* from the terrain, never independently authored, so this is a pure
   * recomputation — which is what lets a sculpt undo restore the plants for free by simply restoring
   * the heights and running this again. `within` limits the work to the stroke's own footprint; a
   * site with a thousand plants should not walk all of them for a two-metre dab. `within` is tested
   * in **terrain-local** metres, matching the grid the sculpt tool reports its edits in.
   */
  regroundPlants(within?: (x: number, z: number) => boolean): void {
    const terrain = this.deps.terrain();
    if (!terrain) return;
    const local = new THREE.Vector2();
    let changed = false;
    for (const instance of this.world.all()) {
      terrain.toLocal(instance.position.x, instance.position.z, local);
      if (within && !within(local.x, local.y)) continue;
      const height = terrain.sampleHeight(local.x, local.y);
      if (Math.abs(height - instance.position.y) < 1e-5) continue;
      instance.position.y = height;
      this.world.update(instance);
      changed = true;
    }
    if (changed) this.refresh();
  }

  /** Subscribe to editor state changes (selection, world contents, tool mode). */
  onChange(listener: EditorChangeListener): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  /** Subscribe to *document* changes only — what autosave and the dirty flag care about. */
  onDocumentChange(listener: EditorDocumentListener): () => void {
    this.documentListeners.add(listener);
    return () => this.documentListeners.delete(listener);
  }

  /** Choose the plant the placement tool will place. Re-arms the ghost if already placing. */
  setActiveDefinition(id: string): void {
    if (!this.deps.catalog.has(id)) return;
    this.activeId = id;
    if (this.placement.isActive) this.placement.start(id);
    this.updateReadout();
    this.emitChange();
  }

  /** Enter placement mode (optionally switching plant first). */
  startPlacement(definitionId?: string): void {
    this.reclaimCanvas();
    if (definitionId !== undefined) this.setActiveDefinition(definitionId);
    this.placement.start(this.activeDefinitionId);
    this.updateReadout();
    this.emitChange();
  }

  cancelPlacement(): void {
    this.placement.cancel();
    this.updateReadout();
    this.emitChange();
  }

  togglePlacement(): void {
    if (this.placement.isActive) this.cancelPlacement();
    else this.startPlacement();
  }

  /** Begin an interactive move/rotate/maturity drag on the current selection. */
  beginTransform(mode: 'move' | 'rotate' | 'maturity'): void {
    if (this.placement.isActive) return;
    this.reclaimCanvas();
    this.transform.begin(mode);
    this.emitChange();
  }

  /** Ask the app for the canvas back when another tool holds it. A no-op when it already has it. */
  private reclaimCanvas(): void {
    if (this.inputEnabled) return;
    this.deps.onRequestCanvas?.();
  }

  /**
   * Apply inspector edits to placed plants as one undoable command. Passing a `mergeKey` collapses
   * successive edits from the same slider drag into a single history entry (spec §16).
   */
  applyInstanceEdits(edits: readonly FloraInstanceEdit[], label: string, mergeKey?: string): void {
    const changes: FloraChange[] = [];
    for (const edit of edits) {
      const before = this.world.get(edit.id);
      if (!before) continue;
      const after: FloraInstance = {
        ...before,
        ...edit.patch,
        position: { ...(edit.patch.position ?? before.position) },
        rotation: { ...(edit.patch.rotation ?? before.rotation) },
      };
      for (const key of edit.clear ?? []) delete after[key];
      changes.push({ before, after });
    }
    if (changes.length === 0) return;
    this.history.execute(new TransformFloraCommand(this.world, changes, label, mergeKey));
    for (const change of changes) this.alias.propagate(change.after.id);
    this.refresh();
  }

  /**
   * Empty the site: plants, groups, selection, and undo history. Opening a project or starting a new
   * one is not an edit of the current site, so the history is dropped rather than made undoable —
   * undoing across a document swap would leave the world and the terrain describing different sites.
   */
  clearSite(): void {
    this.cancelPlacement();
    this.selection.clear();
    this.world.clear();
    this.groups.clear();
    this.history.clear();
    this.refresh();
    this.emitChange();
  }

  /**
   * Populate a (freshly cleared) site from a loaded project. Instances are adopted as-is so ids,
   * alias links, and group membership survive the round trip.
   */
  loadInstances(instances: readonly FloraInstance[], groups: readonly PlantGroup[] = []): void {
    for (const instance of instances) {
      if (this.world.has(instance.id)) continue;
      this.world.add(instance);
    }
    this.groups.load(groups);
    this.refresh();
    this.emitChange();
  }

  private placeInstance(instance: FloraInstance): void {
    this.history.execute(new AddFloraCommand(this.world, instance));
  }

  /**
   * Record a finished brush stroke as one undoable step.
   *
   * `push` rather than `execute`: the plants are already in the world, put there dab by dab so the
   * bed could be seen filling in. Executing here would add every one of them a second time.
   */
  private recordStroke(instances: readonly FloraInstance[]): void {
    const first = instances[0];
    if (!first) return;
    const def = this.deps.catalog.find(first.floraDefinitionId);
    const name = def?.commonNames[0] ?? def?.botanicalName ?? 'plants';
    this.history.push(
      new AddManyFloraCommand(this.world, [...instances], `Plant ${instances.length} ${name.toLowerCase()}`),
    );
    this.refresh();
  }

  private cycleActivePlant(step: number): void {
    const list = this.deps.catalog.list();
    if (list.length === 0) return;
    const current = list.findIndex((def) => def.id === this.activeId);
    const next = list[(((current < 0 ? 0 : current) + step) % list.length + list.length) % list.length];
    if (next) this.setActiveDefinition(next.id);
  }

  /**
   * Delete everything selected, whatever class it belongs to, as one history entry.
   *
   * One entry rather than one per class: the designer made one gesture. A Ctrl+Z that took back the
   * bench but left the four goldfinches that were swept up with it would be describing a step nobody
   * took. `CompositeCommand` undoes its parts in reverse, which costs nothing here — the three
   * worlds are independent — and is the established way to record a single act with several owners.
   */
  deleteSelected(): void {
    const ids = this.selection.selected();
    if (ids.length === 0) return;

    const parts: Command[] = [];

    const instances = ids
      .map((id) => this.world.get(id))
      .filter((inst): inst is FloraInstance => inst !== undefined);
    if (instances.length > 0) parts.push(new RemoveFloraCommand(this.world, instances));

    for (const layer of this.staffage) {
      const items = ids
        .map((id) => layer.get(id))
        .filter((item): item is StaffageRecord => item !== undefined && !item.locked);
      if (items.length > 0) parts.push(layer.removeCommand(items));
    }

    if (parts.length === 0) return;
    this.history.execute(this.oneEntry(parts, `Remove ${ids.length} items`));
    this.pruneSelection();
    this.refresh();
  }

  undo(): void {
    if (this.history.undo()) {
      this.pruneSelection();
      this.refresh();
    }
  }

  redo(): void {
    if (this.history.redo()) {
      this.pruneSelection();
      this.refresh();
    }
  }

  /**
   * Travel to a point in the session's operations history — the history panel's click-to-jump.
   *
   * Routed through here rather than letting the panel call `history.goto` itself, for the same
   * reason {@link undo} and {@link redo} are: crossing several operations can delete plants the
   * selection still names, and the selection and the highlight renderer are this editor's to keep
   * honest. `goto` is one journey and one notification, so this costs one prune however far it went.
   */
  gotoHistory(index: number): void {
    if (this.history.goto(index)) {
      this.pruneSelection();
      this.refresh();
    }
  }

  /**
   * The selected things that are not plants, named.
   *
   * The inspector is a plant inspector and is not about to become three inspectors this pass, but it
   * must not go on saying "select a plant to inspect it" while a bench is plainly selected and
   * highlighted on the canvas. This is the least it needs to say something true.
   */
  selectedStaffage(): { id: string; kind: 'fauna' | 'object'; name: string }[] {
    const found: { id: string; kind: 'fauna' | 'object'; name: string }[] = [];
    for (const id of this.selection.selected()) {
      for (const layer of this.staffage) {
        const item = layer.get(id);
        if (item) {
          found.push({ id, kind: layer.kind, name: layer.describe(item) });
          break;
        }
      }
    }
    return found;
  }

  /* ------------------- Resolving an id across the three classes ------------------- */

  /** The layer owning an id, or null when it is a plant or nothing at all. */
  private layerFor(id: string): StaffageLayer | null {
    for (const layer of this.staffage) {
      if (layer.has(id)) return layer;
    }
    return null;
  }

  /** The scene object an id is drawn as — what the highlight renderer boxes. */
  private viewOf(id: string): THREE.Object3D | undefined {
    return this.world.view(id) ?? this.layerFor(id)?.view(id);
  }

  /** True while an id still names something on the site. */
  private exists(id: string): boolean {
    return this.world.has(id) || this.layerFor(id) !== null;
  }

  /**
   * Drop selected ids that no longer name anything.
   *
   * One method rather than the five copies of `prune(id => world.has(id))` this replaces, because
   * every one of them would otherwise have quietly deselected every creature and object on the site
   * the first time anything was undone.
   */
  private pruneSelection(): void {
    this.selection.prune((id) => this.exists(id));
  }

  /** Several commands as one history entry, or the single one when that is all there is. */
  private oneEntry(parts: readonly Command[], label: string): Command {
    const first = parts[0];
    if (parts.length === 1 && first) return first;
    return new CompositeCommand(parts, label);
  }

  private handleKey(e: KeyboardEvent): void {
    // Ignore while typing in inputs.
    const target = e.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

    const action = resolveShortcut(toShortcutEvent(e));
    if (action === null) return;
    if (!this.inputEnabled && !SHORTCUTS_WHILE_SUSPENDED.includes(action)) return;
    this.dispatchShortcut(action, e);
  }

  private dispatchShortcut(action: ShortcutAction, e: KeyboardEvent): void {
    switch (action) {
      case 'undo':
        e.preventDefault();
        this.undo();
        break;
      case 'redo':
        e.preventDefault();
        this.redo();
        break;
      case 'select-all':
        e.preventDefault();
        this.selectAll();
        break;
      case 'duplicate':
        e.preventDefault();
        this.duplicateSelected();
        break;
      case 'copy':
        this.copySelected();
        break;
      case 'paste':
        this.pasteClipboard();
        break;
      case 'link':
        e.preventDefault();
        this.linkSelected();
        break;
      case 'unlink':
        e.preventDefault();
        this.unlinkSelected();
        break;
      case 'group':
        e.preventDefault();
        this.groupSelected();
        break;
      case 'ungroup':
        e.preventDefault();
        this.ungroupSelected();
        break;
      case 'save':
        e.preventDefault();
        this.deps.onSave?.();
        break;
      case 'save-as':
        e.preventDefault();
        this.deps.onSaveAs?.();
        break;
      case 'rename':
        e.preventDefault();
        this.deps.onRename?.();
        break;
      case 'place':
        this.togglePlacement();
        break;
      case 'plant-mode':
        this.togglePlantMode();
        break;
      case 'prev-plant':
        this.cycleActivePlant(-1);
        break;
      case 'next-plant':
        this.cycleActivePlant(1);
        break;
      case 'move':
        this.beginTransform('move');
        break;
      case 'rotate':
        this.beginTransform('rotate');
        break;
      case 'maturity':
        this.beginTransform('maturity');
        break;
      case 'delete':
        if (!this.placement.isActive) this.deleteSelected();
        break;
      case 'cancel':
        if (this.placement.isActive) this.placement.cancel();
        else if (this.transform.activeMode) this.transform.cancel();
        else this.selection.clear();
        this.updateReadout();
        this.emitChange();
        break;
      case 'focus-selection':
        this.deps.onFocusSelection?.(this.selection.selected());
        break;
      case 'view-perspective':
        this.deps.onSetView?.('perspective');
        break;
      case 'view-top':
        this.deps.onSetView?.('top');
        break;
      case 'view-front':
        this.deps.onSetView?.('front');
        break;
      case 'photo-mode':
        this.deps.onTogglePhotoMode?.();
        break;
      case 'paint-ground':
        this.deps.onPaintTool?.();
        break;
      case 'sculpt-ground':
        this.deps.onSculptTool?.();
        break;
      case 'wand':
        this.deps.onWandTool?.();
        break;
      case 'tag':
        this.deps.onTagTool?.();
        break;
      case 'toggle-grid':
        this.deps.onToggleGrid?.();
        break;
      case 'toggle-topo':
        this.deps.onToggleTopo?.();
        break;
      case 'toggle-pointers':
        this.deps.onTogglePointers?.();
        break;
      case 'toggle-history':
        e.preventDefault();
        this.deps.onToggleHistory?.();
        break;
      default:
        break;
    }
  }

  private handlePick(e: PointerEvent): void {
    if (e.button !== 0 || !this.inputEnabled) return;
    if (this.placement.isActive || this.transform.activeMode) return;

    const id = this.pickAt(e.clientX, e.clientY);
    if (id) {
      this.selection.select(id, e.shiftKey);
      return;
    }

    // Empty space: begin a potential drag-box selection; resolved on pointer up.
    this.boxStart = { x: e.clientX, y: e.clientY };
    this.boxActive = false;
    this.boxAdditive = e.shiftKey;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  private handleBoxMove(e: PointerEvent): void {
    if (!this.boxStart) return;
    const dx = e.clientX - this.boxStart.x;
    const dy = e.clientY - this.boxStart.y;
    if (!this.boxActive && Math.hypot(dx, dy) < 4) return;
    this.boxActive = true;
    this.updateBoxElement(this.boxStart.x, this.boxStart.y, e.clientX, e.clientY);
  }

  private handleBoxUp(e: PointerEvent): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    const start = this.boxStart;
    this.boxStart = null;
    this.removeBoxElement();

    if (this.boxActive && start) {
      const rect = rectFromCorners(start.x, start.y, e.clientX, e.clientY);
      const ids = idsInScreenRect(this.projectedPoints(), rect);
      if (this.boxAdditive) {
        for (const id of ids) this.selection.select(id, true);
      } else {
        this.selection.selectOnly(ids);
      }
    } else if (!this.boxAdditive) {
      this.selection.clear();
    }
    this.boxActive = false;
  }

  private handleDoubleClick(e: MouseEvent): void {
    if (!this.inputEnabled) return;
    if (this.placement.isActive || this.transform.activeMode) return;
    const id = this.pickAt(e.clientX, e.clientY);
    if (!id) return;
    const inst = this.world.get(id);
    if (inst) {
      this.selection.selectOnly(idsOfSpecies(this.world.all(), inst.floraDefinitionId));
      return;
    }
    // The same gesture one class over: every goldfinch, or every bench of that model.
    const layer = this.layerFor(id);
    const item = layer?.get(id);
    if (!layer || !item) return;
    this.selection.selectOnly(layer.siblings(item).filter(isMovable).map((sibling) => sibling.id));
  }

  selectAll(): void {
    const ids = this.world.all().map((inst) => inst.id);
    for (const layer of this.staffage) {
      for (const item of layer.all()) {
        if (isMovable(item)) ids.push(item.id);
      }
    }
    this.selection.selectOnly(ids);
  }

  private selectedInstances(): FloraInstance[] {
    return this.selection
      .selected()
      .map((id) => this.world.get(id))
      .filter((inst): inst is FloraInstance => inst !== undefined);
  }

  private snapToGround(instances: FloraInstance[]): void {
    const terrain = this.deps.terrain();
    if (!terrain) return;
    for (const inst of instances) {
      inst.position.y = terrain.sampleHeight(inst.position.x, inst.position.z);
    }
  }

  private commitAdditions(instances: FloraInstance[], label: string): void {
    if (instances.length === 0) return;
    this.snapToGround(instances);
    this.history.execute(new AddManyFloraCommand(this.world, instances, label));
    this.selection.selectOnly(instances.map((inst) => inst.id));
    this.refresh();
  }

  duplicateSelected(): void {
    const clones = duplicateInstances(this.selectedInstances());
    this.commitAdditions(clones, clones.length === 1 ? 'Duplicate plant' : `Duplicate ${clones.length} plants`);
  }

  copySelected(): void {
    const instances = this.selectedInstances();
    if (instances.length > 0) this.clipboard.copy(instances);
  }

  pasteClipboard(): void {
    if (this.clipboard.isEmpty) return;
    const pasted = this.clipboard.paste();
    this.commitAdditions(pasted, pasted.length === 1 ? 'Paste plant' : `Paste ${pasted.length} plants`);
  }

  // Alias links and groups live on the instances and in the group manager rather than in the
  // history, so each one announces the document change itself.

  linkSelected(): void {
    const groupId = this.alias.link(this.selection.selected());
    if (!groupId) return;
    this.refresh();
    this.emitDocumentChange();
  }

  unlinkSelected(): void {
    const ids = this.selection.selected();
    if (ids.length === 0) return;
    for (const id of ids) this.alias.unlink(id);
    this.refresh();
    this.emitDocumentChange();
  }

  groupSelected(): void {
    if (this.groups.create(this.selection.selected())) this.emitDocumentChange();
  }

  ungroupSelected(): void {
    const primary = this.selection.primary;
    if (primary === null) return;
    const group = this.groups.all().find((g) => g.memberIds.includes(primary));
    if (group && this.groups.delete(group.id)) this.emitDocumentChange();
  }

  /**
   * Raycast the site at a client position and return what was hit, if anything.
   *
   * Both roots in one call so the *nearest* thing wins. Testing flora first and falling back would
   * pick the shrub behind a bench whenever their silhouettes overlap, which is exactly the case a
   * designer is clicking to resolve.
   */
  private pickAt(clientX: number, clientY: number): string | null {
    const rect = this.deps.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.pickRaycaster.setFromCamera(ndc, this.deps.getCamera());
    const roots = [...this.deps.floraRoot.children];
    if (this.deps.staffageRoot) roots.push(...this.deps.staffageRoot.children);
    for (const hit of this.pickRaycaster.intersectObjects(roots, true)) {
      const id = this.resolveInstanceId(hit.object);
      // A locked or hidden creature is see-through to a click: skip it and take whatever is behind.
      if (id === null) continue;
      const item = this.layerFor(id)?.get(id);
      if (item && !isMovable(item)) continue;
      return id;
    }
    return null;
  }

  /**
   * Project every selectable thing's world position to client-space screen points.
   *
   * Everything, not just plants: a drag-box over a corner of the garden should take the bench and
   * the rabbit standing on it, or the box is lying about what it enclosed.
   */
  private projectedPoints(): ScreenPoint[] {
    const rect = this.deps.domElement.getBoundingClientRect();
    const camera = this.deps.getCamera();
    const v = new THREE.Vector3();
    const points: ScreenPoint[] = [];
    const project = (id: string, position: { x: number; y: number; z: number }): void => {
      v.set(position.x, position.y, position.z).project(camera);
      if (v.z > 1) return; // behind the camera
      points.push({
        id,
        x: ((v.x + 1) / 2) * rect.width + rect.left,
        y: ((-v.y + 1) / 2) * rect.height + rect.top,
      });
    };

    for (const inst of this.world.all()) project(inst.id, inst.position);
    for (const layer of this.staffage) {
      for (const item of layer.all()) {
        if (!isMovable(item)) continue;
        // The world's own view knows where the ground put it; the record only carries an offset, and
        // projecting that would box a creature on a hillside at sea level.
        const view = layer.view(item.id);
        project(item.id, view ? view.position : item.position);
      }
    }
    return points;
  }

  private updateBoxElement(ax: number, ay: number, bx: number, by: number): void {
    const parent = this.deps.domElement.parentElement;
    if (!parent) return;
    if (!this.boxElement) {
      this.boxElement = document.createElement('div');
      this.boxElement.className = 'lg-select-box';
      parent.appendChild(this.boxElement);
    }
    const host = parent.getBoundingClientRect();
    const rect = rectFromCorners(ax, ay, bx, by);
    this.boxElement.style.left = `${rect.minX - host.left}px`;
    this.boxElement.style.top = `${rect.minY - host.top}px`;
    this.boxElement.style.width = `${rect.maxX - rect.minX}px`;
    this.boxElement.style.height = `${rect.maxY - rect.minY}px`;
  }

  private removeBoxElement(): void {
    this.boxElement?.remove();
    this.boxElement = null;
  }

  /**
   * Walk up from a raycast hit to the record it belongs to.
   *
   * Each class stamps its own key on the view it builds — `floraInstanceId`, `faunaInstanceId`,
   * `siteObjectId` — and the layer names its own, so a fourth class costs no change here.
   */
  private resolveInstanceId(object: THREE.Object3D | undefined): string | null {
    let current: THREE.Object3D | null = object ?? null;
    while (current) {
      const flora = current.userData.floraInstanceId as unknown;
      if (typeof flora === 'string') return flora;
      for (const layer of this.staffage) {
        const id = current.userData[layer.viewIdKey] as unknown;
        if (typeof id === 'string' && layer.has(id)) return id;
      }
      current = current.parent;
    }
    return null;
  }

  private updateReadout(): void {
    // The ground tools write their own readout while they hold the canvas.
    if (!this.deps.readout || this.placement.isActive || !this.inputEnabled) return;
    const def = this.deps.catalog.find(this.activeDefinitionId);
    if (!def) return;
    const name = def.commonNames[0] ?? def.botanicalName;
    this.deps.readout.textContent = `Active: ${name}  ·  P to place · [ ] to change · click to select`;
  }

  private emitChange(): void {
    for (const listener of this.changeListeners) listener();
  }

  private emitDocumentChange(): void {
    for (const listener of this.documentListeners) listener();
  }

  dispose(): void {
    this.changeListeners.clear();
    this.documentListeners.clear();
    this.deps.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.deps.domElement.removeEventListener('dblclick', this.onDoubleClick);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('keydown', this.onKeyDown);
    this.removeBoxElement();
    this.placement.dispose();
    this.transform.dispose();
    this.selectionRenderer.dispose();
    this.world.dispose();
  }
}
